import React, { useState, forwardRef, useImperativeHandle, Ref } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Hash, Browser, Crop, ShareNetwork, ArrowCounterClockwise } from 'src/common/icons';
import { useAssetRelationshipsQuery } from 'src/api/queries/assetsQueries';
import { Asset, AssetRelationshipEntry, AssetRelationshipsResponse } from 'src/types/TAssets';
import { TabTrigger } from './components/TabTrigger';
import { formatCoreData } from './helpers/formatCoreData';
import { config } from 'src/utils/config';
import styles from './Modal.module.scss';
import { AssetMetadata } from './components/AssetMetadata';
import { ConversionData, EDITION_MODE, SubtitleData } from './ModalEditor';
import ConversionsTab from './ConversionsTab/ConversionsTab';
import VersionsTab from './VersionsTab/VersionsTab';
import ReactTooltip from 'react-tooltip';

const tabs = {
  metadata: 'metadata',
  core: 'core',
  conversions: 'conversions',
  relationships: 'relationships',
  versions: 'versions',
};

// `?tab=versions` (e.g. from a release-task notification) opens that tab.
const initialTab = (search: string) => {
  const requested = new URLSearchParams(search).get('tab');
  return requested && Object.values(tabs).includes(requested) ? requested : tabs.metadata;
};

export const ModalSidebar = forwardRef(
  (
    {
      asset,
      schemas,
      onEditConversion,
    }: {
      asset: Asset;
      schemas: any;
      onEditConversion: (type: EDITION_MODE, data: ConversionData | SubtitleData) => void;
    },
    ref: Ref<{ toggleSidebar: (forceState?: boolean) => void }>,
  ) => {
    const { data_schema_id = config.defaultSchemaId, ...metadata } = asset?.data ?? {};

    const location = useLocation<any>();
    const [activeTab, setActiveTab] = useState(() => initialTab(location.search));
    const [showSidebar, setShowSidebar] = useState<boolean>(true);

    let assetMetadata: [string, string | number][] = [];

    assetMetadata.push(['name', asset.name]);
    assetMetadata.push(['size', asset.size]);
    assetMetadata.push(['checksum', asset.checksum]);
    assetMetadata.push(['content type', asset.content_type]);
    if (asset.creator_id) {
      assetMetadata.push(['creator id', asset.creator_id]);
    }
    assetMetadata.push(['Upload Time', new Date(asset.upload_time).toLocaleString()]);
    if (asset.update_time) {
      assetMetadata.push(['Update Time', new Date(asset.update_time).toLocaleString()]);
    }
    assetMetadata.push(['public', asset.public.toString()]);

    assetMetadata = [
      ...assetMetadata,
      ...Object.entries(asset.metadata).filter(([title]) => {
        if (title.startsWith('_filespin')) {
          return false;
        }
        if (title === 'width' || title === 'height') {
          return asset.content_type.startsWith('video') || asset.content_type.startsWith('image');
        }
        if (title === 'duration') {
          return asset.content_type.startsWith('video') || asset.content_type.startsWith('audio');
        }
        if (title === 'dpi') {
          return asset.content_type.startsWith('image');
        }

        return title !== 'ColorPalette';
      }),
    ];

    const history = useHistory<any>();

    // Only fetch relationships when the Relationships tab is open (keeps it off the hot path).
    const { data: relationshipsResponse, isLoading: relationshipsLoading } = useAssetRelationshipsQuery(asset.id, undefined, {
      enabled: activeTab === tabs.relationships,
    });
    const relationships: AssetRelationshipsResponse | undefined = relationshipsResponse?.data;

    // Navigate to a related asset, keeping the modal overlay stacked on top of
    // the gallery so "back" returns to the gallery (same pattern as the
    // gallery card / prev-next navigation).
    const navigateToRelatedAsset = (relatedAssetId: string) => {
      history.replace({
        pathname: `/asset/${relatedAssetId}`,
        state: { background: location?.state?.background ?? location },
      });
    };

    useImperativeHandle(ref, () => ({
      toggleSidebar: (forceState?: boolean) => {
        setShowSidebar((old) => forceState ?? !old);
      },
    }));

    // Add-ons that actually ran, shown as pills at the bottom of Core data
    // (replaces the old Add-on tab, which served no end-user purpose).
    const processedAddons = Object.entries(asset.addons_info || {})
      .filter(([, value]) => value?.available)
      .map(([name]) => name);

    const humanizeAddonName = (name: string) =>
      name
        .toLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

    const CoreTab = () => (
      <div>
        {assetMetadata.map(([title, value]) => (
          <div className={styles.metadataRow} key={title}>
            <div>{title}</div>
            <div className={styles.metadataValue}>{formatCoreData(title, value)}</div>
          </div>
        ))}
        {asset.errors?.original && <div className={styles.errorDesc}>{asset.errors?.original}</div>}
        {processedAddons.length > 0 && (
          <div className={styles.addonPillsSection}>
            <div className={styles.addonPillsTitle}>Processed add-ons</div>
            <div className={styles.addonPills}>
              {processedAddons.map((name) => (
                <span className={styles.addonPill} key={name} title={name}>
                  {humanizeAddonName(name)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
    
    const RelationshipsTab = () => {
      if (relationshipsLoading) {
        return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '17rem' }}>
            <p style={{ fontWeight: 400, fontSize: 'var(--font-size-xl)' }}>Loading relationships…</p>
          </div>
        );
      }

      const rows: { relation: string; entry: AssetRelationshipEntry }[] = [];
      if (relationships) {
        relationships.parents.forEach((entry) => rows.push({ relation: 'Parent', entry }));
        relationships.children.forEach((entry) => rows.push({ relation: 'Child', entry }));
        relationships.related.forEach((entry) => rows.push({ relation: 'Related', entry }));
        relationships.custom.forEach((entry) => rows.push({ relation: 'Other', entry }));
      }
      const visibleRows = rows.filter((row) => row.entry.asset);

      if (visibleRows.length === 0) {
        return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '17rem' }}>
            <p style={{ fontWeight: 400, fontSize: 'var(--font-size-xl)' }}>No related assets</p>
          </div>
        );
      }

      return (
        <table className={styles.linkedTable}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Relation</th>
              <th>Type</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map(({ relation, entry }) => {
              const detail =
                [entry.subtype, entry.origin === 'auto' ? 'auto' : null].filter(Boolean).join(' · ') ||
                entry.asset!.content_type;
              return (
                <tr key={entry.relationship_id}>
                  <td>
                    <button
                      type="button"
                      className={styles.linkedAssetButton}
                      onClick={() => navigateToRelatedAsset(entry.asset!.id)}
                      data-tip="Open related asset"
                      title={entry.asset!.id}
                    >
                      <ReactTooltip effect="solid" />
                      {entry.asset!.name || entry.asset!.id}
                    </button>
                  </td>
                  <td>{relation}</td>
                  <td>{detail}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    };

    return (
      <>
        {showSidebar && (
          <div className={styles.details}>
            <div className={styles.tabs}>
              <TabTrigger keyName={tabs.core} activeKey={activeTab} setKey={setActiveTab} />
              <TabTrigger keyName={tabs.metadata} activeKey={activeTab} setKey={setActiveTab} />
              <TabTrigger keyName={tabs.conversions} activeKey={activeTab} setKey={setActiveTab} />
              <TabTrigger keyName={tabs.relationships} activeKey={activeTab} setKey={setActiveTab} />
              <TabTrigger keyName={tabs.versions} activeKey={activeTab} setKey={setActiveTab} />
            </div>
            <div className={styles.scroller}>
              {activeTab === tabs.core && <CoreTab />}
              {activeTab === tabs.metadata && <AssetMetadata asset={asset} schemas={schemas} />}
              {activeTab === tabs.conversions && (
                <ConversionsTab asset={asset} onEditConversion={onEditConversion} />
              )}
              {activeTab === tabs.relationships && <RelationshipsTab />}
              {activeTab === tabs.versions && <VersionsTab asset={asset} />}
            </div>
          </div>
        )}
        {!showSidebar && (
          <div className={styles.tabButtons} onClick={() => setShowSidebar(true)}>
            <TabTrigger keyName={tabs.metadata} activeKey={activeTab} setKey={setActiveTab} icon={<Hash />} />
            <TabTrigger keyName={tabs.core} activeKey={activeTab} setKey={setActiveTab} icon={<Browser />} />
            <TabTrigger keyName={tabs.conversions} activeKey={activeTab} setKey={setActiveTab} icon={<Crop />} />
            <TabTrigger keyName={tabs.relationships} activeKey={activeTab} setKey={setActiveTab} icon={<ShareNetwork />} />
            <TabTrigger keyName={tabs.versions} activeKey={activeTab} setKey={setActiveTab} icon={<ArrowCounterClockwise />} />
          </div>
        )}
      </>
    );
  },
);